import { type FormEvent, type JSX, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import {
  Badge,
  Button,
  Card,
  Input,
  LiveDot,
  Skeleton,
  Spinner,
  Textarea,
} from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { messageDisplayStatus, useMessagesList, useSendMessage } from '@/hooks/useMessages';
import { useSendersList, type SenderItem } from '@/hooks/useSenders';
import { ApiError } from '@/lib/api';
import { can } from '@/lib/permissions';
import { formatCurrency, formatRelativeTime, truncate } from '@/lib/format';
import { calculateSegments, isValidE164 } from '@/lib/sms';
import { cn } from '@/lib/utils';

// A sender is usable for sending iff its registration_status leaves it
// active. 'approved' (US 10DLC) and 'not_required' (non-US) both let
// outbound through; other statuses (pending/submitted/rejected/suspended)
// would just produce a 403 at the backend, so we hide them from the picker.
function isUsableSender(s: SenderItem): boolean {
  return s.registration_status === 'approved' || s.registration_status === 'not_required';
}

type MessageType = 'transactional' | 'marketing' | '2fa';

interface FormState {
  to: string;
  from: string;
  body: string;
  messageType: MessageType;
}

const INITIAL_FORM: FormState = {
  to: '',
  from: '', // auto-picked from the user's senders list once it loads
  body: '',
  messageType: 'transactional',
};

interface FieldErrors {
  to?: string;
  from?: string;
  body?: string;
}

function useTabVisibility(): boolean {
  // Pause polling while the tab is hidden so we don't burn API calls or the
  // user's data. Default to "visible" for SSR / odd environments.
  const [visible, setVisible] = useState<boolean>(() =>
    typeof document === 'undefined' ? true : document.visibilityState === 'visible',
  );
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onChange = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

type StatusTone = 'queued' | 'sent' | 'delivered' | 'failed' | 'rejected' | 'unknown';

function statusTone(status: string): StatusTone {
  switch (status) {
    case 'queued':
    case 'pending':
    case 'queueing':
      return 'queued';
    case 'sent':
    case 'sending':
      return 'sent';
    case 'delivered':
      return 'delivered';
    case 'failed':
      return 'failed';
    case 'rejected':
      return 'rejected';
    default:
      return 'unknown';
  }
}

function StatusBadge({ status, t }: { status: string; t: (k: string) => string }): JSX.Element {
  const tone = statusTone(status);
  const labelKey = `msg_status_${tone}`;
  const fallback = labelKey === `msg_status_unknown` ? status : undefined;
  const label = fallback ?? t(labelKey);
  const variant: 'gray' | 'info' | 'success' | 'danger' | 'volt' =
    tone === 'delivered'
      ? 'success'
      : tone === 'failed' || tone === 'rejected'
        ? 'danger'
        : tone === 'sent'
          ? 'info'
          : tone === 'queued'
            ? 'volt'
            : 'gray';
  const dotClass =
    tone === 'queued'
      ? 'bg-volt animate-volt-pulse'
      : tone === 'sent'
        ? 'bg-info'
        : tone === 'delivered'
          ? 'bg-success'
          : tone === 'failed' || tone === 'rejected'
            ? 'bg-danger'
            : 'bg-text-4';
  return (
    <Badge variant={variant} className="gap-1.5">
      <span aria-hidden="true" className={cn('w-2 h-2 rounded-full', dotClass)} />
      {label}
    </Badge>
  );
}

export default function MessagesPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);
  const { user } = useAuth();
  // Viewer role lands on this page (Messages is in their navigation)
  // but should NOT see the send form — only the historical list.
  // Backend RequireRoleMin("operator") on POST /messages enforces it
  // server-side; hiding the form is the matching UX.
  const canSend = can.sendMessages(user?.role);

  const visible = useTabVisibility();
  const list = useMessagesList({ enabled: visible });
  const send = useSendMessage();
  const senders = useSendersList();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  const seg = useMemo(() => calculateSegments(form.body), [form.body]);

  const usableSenders = useMemo<SenderItem[]>(
    () => (senders.data?.items ?? []).filter(isUsableSender),
    [senders.data],
  );

  // Auto-pick a default sender once the list loads. Also re-picks if the
  // currently-selected one is removed (e.g. operator deleted it from
  // /senders in another tab) so the form never sits on a stale value the
  // backend would 403.
  useEffect(() => {
    if (usableSenders.length === 0) return;
    setForm((prev) => {
      const stillValid = usableSenders.some((s) => s.value === prev.from);
      if (stillValid) return prev;
      return { ...prev, from: usableSenders[0].value };
    });
  }, [usableSenders]);

  const validate = (values: FormState): FieldErrors => {
    const next: FieldErrors = {};
    if (!values.to.trim()) {
      next.to = t('msg_err_to_required');
    } else if (!isValidE164(values.to)) {
      next.to = t('msg_err_to_e164');
    }
    if (!values.from.trim()) {
      next.from = t('msg_err_from_required');
    }
    if (!values.body.trim()) {
      next.body = t('msg_err_body_required');
    }
    return next;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(null);

    const fieldErrors = validate(form);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    try {
      const res = await send.mutateAsync({
        to: form.to.trim(),
        from: form.from.trim(),
        body: form.body,
        message_type: form.messageType,
      });
      setSubmitSuccess(t('msg_sent_ok', { id: res.id.slice(0, 8) }));
      // Reset body but keep `to` + `from` so resending to the same recipient is quick.
      setForm((prev) => ({ ...prev, body: '' }));
    } catch (err) {
      if (err instanceof ApiError) {
        // Backend error codes from messages_handler.go → human messages
        const codeMap: Record<string, string> = {
          invalid_phone_number: t('msg_err_to_e164'),
          sender_not_approved: t('msg_err_sender_not_approved'),
          sender_not_found: t('msg_err_sender_not_found'),
          no_route_available: t('msg_err_no_route'),
          phone_in_dnc: t('msg_err_dnc'),
          outside_tcpa_window: t('msg_err_tcpa'),
          insufficient_balance: t('msg_err_insufficient_balance'),
          reseller_insufficient_balance: t('msg_err_reseller_insufficient_balance'),
          tenant_suspended: t('msg_err_tenant_suspended'),
        };
        setSubmitError(codeMap[err.code] ?? err.message ?? t('msg_err_generic'));
      } else if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError(t('msg_err_generic'));
      }
    }
  };

  const items = list.data?.items ?? [];

  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto">
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-tight m-0">
            {t('nav_messages')}
          </h1>
          <p className="text-[13px] text-text-3 mt-1">{t('msg_page_sub')}</p>
        </div>
        {visible && list.isFetching && (
          <span className="inline-flex items-center gap-1.5 text-[12px] text-text-3">
            <LiveDot className="w-1.5 h-1.5" />
            {t('msg_polling')}
          </span>
        )}
      </header>

      {/* Send form — hidden for viewer role (read-only). */}
      {canSend && (
      <Card padding="lg" className="mb-6">
        <h2 className="display text-[16px] font-semibold text-text mb-4">{t('msg_send_title')}</h2>

        {submitError && (
          <div
            role="alert"
            className="flex gap-2.5 items-start px-3.5 py-3 bg-danger-bg border border-danger-bd rounded-md mb-4"
          >
            <Icon.warn width="18" height="18" className="text-danger flex-shrink-0 mt-px" />
            <div className="text-[13px] text-danger leading-relaxed">{submitError}</div>
          </div>
        )}

        {submitSuccess && (
          <div className="flex gap-2.5 items-start px-3.5 py-3 bg-success-bg border border-success-bd rounded-md mb-4">
            <span className="text-[13px] text-success leading-relaxed">{submitSuccess}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="msg-to" className="block text-[12px] font-medium text-text-2 mb-1.5">
                {t('msg_to_label')}
              </label>
              <Input
                id="msg-to"
                placeholder="+15551234567"
                value={form.to}
                onChange={(e) => setForm((prev) => ({ ...prev, to: e.target.value }))}
                error={Boolean(errors.to)}
                disabled={send.isPending}
                autoComplete="tel"
              />
              {errors.to ? (
                <p className="text-[12px] text-danger mt-1">{errors.to}</p>
              ) : (
                <p className="text-[12px] text-text-4 mt-1">{t('msg_to_help')}</p>
              )}
            </div>
            <div>
              <label htmlFor="msg-from" className="block text-[12px] font-medium text-text-2 mb-1.5">
                {t('msg_from_label')}
              </label>
              {senders.isLoading ? (
                <Skeleton className="h-10 w-full rounded-md" />
              ) : usableSenders.length === 0 ? (
                <div className="h-10 px-3 flex items-center text-sm text-text-3 bg-surface-2 border border-line rounded-md">
                  {t('msg_from_empty')}{' '}
                  <Link to="/senders" className="ml-1 text-volt-500 font-medium hover:underline">
                    {t('msg_from_empty_cta')}
                  </Link>
                </div>
              ) : (
                <select
                  id="msg-from"
                  value={form.from}
                  onChange={(e) => setForm((prev) => ({ ...prev, from: e.target.value }))}
                  disabled={send.isPending}
                  className={cn(
                    'h-10 w-full px-3 text-sm bg-surface text-text border rounded-md outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow disabled:opacity-50',
                    errors.from ? 'border-danger' : 'border-line-strong',
                  )}
                >
                  {usableSenders.map((s) => (
                    <option key={s.id} value={s.value}>
                      {s.value} · {s.country_code}
                    </option>
                  ))}
                </select>
              )}
              {errors.from ? (
                <p className="text-[12px] text-danger mt-1">{errors.from}</p>
              ) : (
                <p className="text-[12px] text-text-4 mt-1">{t('msg_from_help')}</p>
              )}
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="msg-type" className="block text-[12px] font-medium text-text-2 mb-1.5">
              {t('msg_type_label')}
            </label>
            <select
              id="msg-type"
              value={form.messageType}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, messageType: e.target.value as MessageType }))
              }
              disabled={send.isPending}
              className="h-10 px-3 text-sm bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow disabled:opacity-50"
            >
              <option value="transactional">{t('msg_type_transactional')}</option>
              <option value="marketing">{t('msg_type_marketing')}</option>
              <option value="2fa">{t('msg_type_2fa')}</option>
            </select>
          </div>

          <div className="mb-4">
            <label htmlFor="msg-body" className="block text-[12px] font-medium text-text-2 mb-1.5">
              {t('msg_body_label')}
            </label>
            <Textarea
              id="msg-body"
              placeholder={t('msg_body_placeholder')}
              value={form.body}
              onChange={(e) => setForm((prev) => ({ ...prev, body: e.target.value }))}
              error={Boolean(errors.body)}
              disabled={send.isPending}
              rows={4}
            />
            <div className="flex items-center justify-between mt-1.5 text-[12px]">
              {errors.body ? (
                <span className="text-danger">{errors.body}</span>
              ) : (
                <span className="text-text-4">{t('msg_body_help')}</span>
              )}
              <span className="num text-text-3">
                {seg.characters} · {seg.encoding} ·{' '}
                {t('msg_segments_count', { count: seg.segments })}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3">
            <Button type="submit" disabled={send.isPending || usableSenders.length === 0}>
              {send.isPending ? (
                <>
                  <Spinner />
                  <span>{t('msg_sending')}</span>
                </>
              ) : (
                <>
                  <span>{t('msg_send_button')}</span>
                  <Icon.arrowRight width="16" height="16" />
                </>
              )}
            </Button>
          </div>
        </form>
      </Card>
      )}

      {/* Recent messages list */}
      <Card padding="none">
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <div>
            <h2 className="display text-[16px] font-semibold text-text">
              {t('msg_recent_title')}
            </h2>
            <p className="text-[12px] text-text-3 mt-0.5">
              {t('msg_recent_count', { count: list.data?.total ?? 0 })}
            </p>
          </div>
        </div>

        {list.isLoading ? (
          <div className="p-5 space-y-2.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : list.isError ? (
          <div className="p-8 text-center">
            <p className="text-[13px] text-danger">{t('msg_load_error')}</p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={() => void list.refetch()}
            >
              {t('msg_retry')}
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-[14px] text-text-2 font-medium">{t('msg_empty_title')}</p>
            <p className="text-[13px] text-text-3 mt-1">{t('msg_empty_sub')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                  <th className="px-5 py-2.5 font-medium">{t('msg_col_when')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('msg_col_to')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('msg_col_body')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('msg_col_status')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('msg_col_seg')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('msg_col_cost')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((m) => (
                  <tr key={m.id} className="border-b border-line last:border-0 hover:bg-surface-2">
                    <td className="px-5 py-3 text-text-3 whitespace-nowrap">
                      <span title={new Date(m.created_at).toLocaleString(lang)}>
                        {formatRelativeTime(m.created_at, lang)}
                      </span>
                    </td>
                    <td className="px-5 py-3 num text-text whitespace-nowrap">{m.to}</td>
                    <td className="px-5 py-3 text-text-2 max-w-[360px]">
                      <span title={m.body}>{truncate(m.body, 80)}</span>
                      {m.error_message && (
                        <div className="text-[11px] text-danger mt-0.5" title={m.error_code ?? ''}>
                          {m.error_message}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <StatusBadge
                        status={messageDisplayStatus(m.status, m.provider_supports_dlr)}
                        t={t}
                      />
                    </td>
                    <td className="px-5 py-3 num text-right text-text-2">{m.segments}</td>
                    <td className="px-5 py-3 num text-right text-text-2">
                      {/* From the tenant's POV, the "cost" of a message
                          is what they pay = sell_price. m.cost is the
                          platform's wholesale buy_cost — exposing it
                          here was misleading (showed $0.0070 even
                          though the balance was debited $0.0090). */}
                      {formatCurrency(m.price, lang)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
